import React, { useEffect, useState } from "react";
import { Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { FiTrash2 } from "react-icons/fi";
import PageLayout from "../../layouts/PageLayout";
import GalleryUpload from "./UploadImages";

const normalizeAttributes = (attributes) => {
    if (Array.isArray(attributes)) {
        return attributes.map((attribute) => ({
            name: String(attribute?.name ?? ""),
            value: String(attribute?.value ?? ""),
            suggestions: [],
            isCustom: false,
        }));
    }

    if (attributes && typeof attributes === "object") {
        return Object.entries(attributes).map(([name, value]) => ({
            name: String(value && typeof value === "object" ? value.name ?? name : name),
            value: String(value && typeof value === "object" ? value.value ?? "" : value ?? ""),
            suggestions: [],
            isCustom: false,
        }));
    }

    return [];
};

export default function ListerEditProductPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();

    const [passedItem, setPassedItem] = useState(location.state?.item || null);

    // =========================================================
    // STATES
    // =========================================================
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    // Brand typed or picked by the lister. A name not in the list is a new
    // brand; the backend adds it when the product is saved.
    const [brandInput, setBrandInput] = useState("");
    const [stores, setStores] = useState([]);

    const [attributeValues, setAttributeValues] = useState([]);
    const [activeAttributeSuggestion, setActiveAttributeSuggestion] = useState(null);

    const [selectedParentCategory, setSelectedParentCategory] = useState("");
    const [availableSubCategories, setAvailableSubCategories] = useState([]);

    const [formData, setFormData] = useState({
        id: "",
        product_code: "",
        title: "",
        product_qty: 1,
        product_condition: "new",
        selling_style: "Fixed",
        sell_price_dollar: "",
        auction_min_price: "",
        short_description: "",
        description: "",
        sub_category_id: "",
        reject_reason: null,
    });

    const [mainImage, setMainImage] = useState(null);
    const [mainImagePreview, setMainImagePreview] = useState(null);
    const [firstImagePreview, setFirstImagePreview] = useState(null);
    const [existingImages, setExistingImages] = useState([]);

    const authHeaders = {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    };

    // =========================================================
    // FETCH INITIAL DATA & PRODUCT DATA
    // =========================================================
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                const initResponse = await axios.get(`${process.env.REACT_APP_API_URL}/lister/initial-data`, {
                    headers: authHeaders,
                });

                const resData = initResponse.data.data || initResponse.data;
                const fetchedCategories = resData.product_categories || [];

                setCategories(fetchedCategories);
                setBrands(resData.brands || []);
                setStores(resData.stores || []);

                let productData = passedItem;
                if (!productData && id) {
                    const prodResponse = await axios.get(`${process.env.REACT_APP_API_URL}/lister/products/${id}`, {
                        headers: authHeaders,
                    });
                    productData = prodResponse.data.data || prodResponse.data;
                    setPassedItem(productData);
                }

                if (productData) {
                    const subCatId = productData.category?.id || "";

                    const productAttributes = normalizeAttributes(productData.attributes);
                    setFormData({
                        id: productData.id || "",
                        product_code: productData.product_code || "",
                        title: productData.title || "",
                        product_qty: productData.product_qty ?? 1,
                        product_condition: productData.product_condition || "new",
                        selling_style: productData.selling_style || "Fixed",
                        sell_price_dollar: productData.sell_price_dollar ?? "",
                        auction_min_price: productData.auction_min_price ?? productData.pricing?.auction_min_price ?? "",
                        short_description: productData.short_description || "",
                        description: productData.description || "",
                        sub_category_id: subCatId,
                        reject_reason: productData.reject_reason || null,
                    });
                    setAttributeValues(productAttributes);
                    setBrandInput(productData.brand?.name || "");

                    setFirstImagePreview(productData.first_img || null);
                    setMainImagePreview(productData.card_img ? process.env.REACT_APP_IMG_URL + productData.card_img : null);
                    setExistingImages(productData.images || []);

                    if (subCatId && fetchedCategories.length > 0) {
                        for (const cat of fetchedCategories) {
                            const foundChild = cat.child_categories?.find(
                                (child) => String(child.id) === String(subCatId)
                            );
                            if (foundChild) {
                                setSelectedParentCategory(String(cat.id));
                                setAvailableSubCategories(cat.child_categories || []);
                                break;
                            }
                        }
                    }
                }
            } catch (err) {
                console.error("Error loading data:", err);
                setError("Failed to load product or initial form data.");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id]);

    // Fetch dynamic attributes when sub_category_id changes
    useEffect(() => {
        let isCurrentCategory = true;

        const fetchAttributes = async () => {
            if (!formData.sub_category_id) {
                return;
            }
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/categories/${formData.sub_category_id}/attributes`,
                    { headers: authHeaders }
                );
                const attrs = (response.data.attributes || [])
                    .map((attribute) => ({
                        name: typeof attribute === "string" ? attribute : attribute?.name,
                        suggestions: Array.isArray(attribute?.values)
                            ? attribute.values.map(String)
                            : [],
                    }))
                    .filter((attribute) => attribute.name);
                if (!isCurrentCategory) {
                    return;
                }
                setAttributeValues((prev) => {
                    const existingByName = new Map(
                        prev
                            .filter((attribute) => attribute.name.trim())
                            .map((attribute) => [attribute.name.trim().toLowerCase(), attribute])
                    );
                    return attrs.map(({ name, suggestions }) => {
                        const existing = existingByName.get(name.trim().toLowerCase());
                        return {
                            name,
                            value: existing?.value ?? "",
                            suggestions,
                            isCustom: false,
                        };
                    });
                });
            } catch (err) {
                if (isCurrentCategory) {
                    console.error("Error fetching category attributes:", err);
                }
            }
        };

        fetchAttributes();

        return () => {
            isCurrentCategory = false;
        };
    }, [formData.sub_category_id]);

    const handleParentCategoryChange = (e) => {
        const parentId = e.target.value;
        setSelectedParentCategory(parentId);

        const selectedCat = categories.find((cat) => String(cat.id) === String(parentId));
        const children = selectedCat ? selectedCat.child_categories || [] : [];

        setAvailableSubCategories(children);
        setFormData((prev) => ({ ...prev, sub_category_id: "" }));
        setAttributeValues([]);
    };

    const normalizeBrand = (name) => String(name || "").trim().replace(/\s+/g, " ").toLowerCase();
    const matchedBrand = brands.find((brand) => normalizeBrand(brand.name) === normalizeBrand(brandInput));
    const isNewBrand = brandInput.trim() !== "" && !matchedBrand;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAttributeChange = (indexToUpdate, value) => {
        setAttributeValues((prev) => prev.map((attribute, index) => (
            index === indexToUpdate ? { ...attribute, ...value } : attribute
        )));
    };

    const addAttribute = () => {
        setAttributeValues((prev) => [...prev, { name: "", value: "", suggestions: [], isCustom: true }]);
    };

    const removeAttribute = (indexToRemove) => {
        setAttributeValues((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleDescriptionChange = (content) => {
        setFormData((prev) => ({ ...prev, description: content }));
    };

    const handleShortDescriptionChange = (content) => {
        setFormData((prev) => ({ ...prev, short_description: content }));
    };

    const handleMainImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setMainImage(file);
        setMainImagePreview(URL.createObjectURL(file));
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            setError("Product title is required.");
            return false;
        }
        if (!formData.sub_category_id) {
            setError("Sub-category is required.");
            return false;
        }
        if (
            formData.sell_price_dollar === null ||
            formData.sell_price_dollar === undefined ||
            formData.sell_price_dollar === "" ||
            Number(formData.sell_price_dollar) <= 0
        ) {
            setError("Selling Price ($) is required and must be greater than 0.");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!validateForm()) return;

        setSaving(true);

        try {
            const submitData = new FormData();

            Object.keys(formData).forEach((key) => {
                if (key === "auction_min_price" && formData.selling_style !== "Auction") {
                    return;
                }
                if (formData[key] !== null && formData[key] !== undefined) {
                    submitData.append(key, formData[key]);
                }
            });

            attributeValues.forEach((attribute, index) => {
                submitData.append(`attributes[${index}][name]`, attribute.name);
                submitData.append(`attributes[${index}][value]`, attribute.value);
            });

            if (mainImage) {
                submitData.append("card_img", mainImage);
            }

            // Existing brand -> its id. New name -> created by the backend on save.
            if (matchedBrand) {
                submitData.append("brand_id", matchedBrand.id);
            } else if (brandInput.trim() !== "") {
                submitData.append("brand_name", brandInput.trim().replace(/\s+/g, " "));
            }

            submitData.append("_method", "PUT");

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/lister/products/${formData.id}`,
                submitData,
                {
                    headers: {
                        ...authHeaders,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const resData = response.data;

            if (!resData.success) {
                setError(resData.message || "Failed to update product.");
                return;
            }

            setSuccess("Product updated successfully.");
            navigate("/lister-products");

        } catch (err) {
            console.error("Update product error:", err);
            if (err.response?.data?.errors) {
                const messages = Object.values(err.response.data.errors).flat().join(" ");
                setError(messages);
            } else {
                setError(err.response?.data?.message || "Failed to update product.");
            }
        } finally {
            setSaving(false);
        }
    };

    const editorButtonList = [
        ['undo', 'redo'],
        ['font', 'fontSize', 'formatBlock'],
        ['bold', 'underline', 'italic', 'strike'],
        ['removeFormat'],
        ['outdent', 'indent'],
        ['align', 'horizontalRule', 'list'],
        ['table', 'link', 'image'],
        ['fullScreen', 'codeView']
    ];

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-3 text-muted">Loading product details...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Edit Product</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to="#" className="mc-breadcrumb-link">Home</Link></li>
                                <li className="mc-breadcrumb-item">Products</li>
                                <li className="mc-breadcrumb-item">Edit Product</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                    {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
                </Col>

                <Col xl={12}>
                    <Form onSubmit={handleSubmit} autoComplete="off">
                        {/* CATEGORIES SELECTION */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Categories Selection</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Parent Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Select value={selectedParentCategory} onChange={handleParentCategoryChange}>
                                                <option value="">Select Parent Category</option>
                                                {categories.map((cat) => (
                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sub Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                name="sub_category_id"
                                                value={formData.sub_category_id}
                                                onChange={handleChange}
                                                disabled={!selectedParentCategory}
                                                required
                                            >
                                                <option value="">Select Sub Category</option>
                                                {availableSubCategories.map((sub) => (
                                                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-2" controlId="lister-brand">
                                            <Form.Label>Brand</Form.Label>
                                            <Form.Control
                                                type="text"
                                                list="lister-brand-options"
                                                value={brandInput}
                                                onChange={(e) => setBrandInput(e.target.value)}
                                                placeholder="Search or type a brand name"
                                                autoComplete="off"
                                                maxLength={255}
                                            />
                                            <datalist id="lister-brand-options">
                                                {brands.map((brand) => (
                                                    <option key={brand.id} value={brand.name} />
                                                ))}
                                            </datalist>
                                            <Form.Text className={isNewBrand ? "text-primary fw-semibold" : "text-muted"}>
                                                {isNewBrand
                                                    ? `"${brandInput.trim()}" is a new brand. It will be added when you save.`
                                                    : matchedBrand
                                                        ? "Existing brand."
                                                        : "Pick a brand from the list or type a new one."}
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* PRODUCT ATTRIBUTES */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Product Attributes</h5>
                                <Button type="button" variant="primary" size="sm" onClick={addAttribute}>
                                    Add Attribute
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-2">
                                    {attributeValues.map((attribute, index) => (
                                        <Col md={4} className="mb-1" key={index}>
                                            <Row className="g-1 align-items-center">
                                                <Col xs={4}>
                                                    <Form.Group>
                                                        {attribute.isCustom ? (
                                                            <Form.Control
                                                                type="text"
                                                                value={attribute.name}
                                                                onChange={(e) => handleAttributeChange(index, { name: e.target.value })}
                                                                placeholder="Enter name..."
                                                            />
                                                        ) : (
                                                            <Form.Control
                                                                plaintext
                                                                readOnly
                                                                value={attribute.name}
                                                                aria-label="Attribute name"
                                                                className="fw-semibold"
                                                            />
                                                        )}
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={6}>
                                                    <Form.Group className="position-relative">
                                                        <Form.Control
                                                            type="text"
                                                            autoComplete="off"
                                                            value={attribute.value}
                                                            onChange={(e) => handleAttributeChange(index, { value: e.target.value })}
                                                            placeholder="Enter or select value..."
                                                            aria-label={`${attribute.name} value`}
                                                            aria-expanded={activeAttributeSuggestion === index}
                                                            aria-controls={`attribute-suggestions-${index}`}
                                                            onFocus={() => setActiveAttributeSuggestion(index)}
                                                            onBlur={() => setActiveAttributeSuggestion(null)}
                                                        />
                                                        {activeAttributeSuggestion === index && attribute.suggestions?.length > 0 && (
                                                            <div
                                                                id={`attribute-suggestions-${index}`}
                                                                className="dropdown-menu show w-100 py-2 mt-1 overflow-auto"
                                                                role="listbox"
                                                                style={{ zIndex: 1050, maxHeight: 220 }}
                                                            >
                                                                {attribute.suggestions.some((value) =>
                                                                    value.toLowerCase().includes(attribute.value.toLowerCase())
                                                                ) ? attribute.suggestions
                                                                    .filter((value) => value.toLowerCase().includes(attribute.value.toLowerCase()))
                                                                    .map((value) => (
                                                                        <button
                                                                            key={value}
                                                                            type="button"
                                                                            className="dropdown-item px-3"
                                                                            role="option"
                                                                            aria-selected={value === attribute.value}
                                                                            style={{ paddingTop: 10, paddingBottom: 10 }}
                                                                            onMouseDown={(event) => event.preventDefault()}
                                                                            onClick={() => {
                                                                                handleAttributeChange(index, { value });
                                                                                setActiveAttributeSuggestion(null);
                                                                            }}
                                                                        >
                                                                            {value}
                                                                        </button>
                                                                    )) : (
                                                                    <div className="px-3 py-2 text-muted small">
                                                                        No matching suggestions
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={2} className="d-flex align-items-center">
                                                    <Button
                                                        type="button"
                                                        variant="danger"
                                                        className="d-inline-flex align-items-center justify-content-center p-0"
                                                        style={{ width: 38, height: 38, minWidth: 38 }}
                                                        aria-label="Remove attribute"
                                                        title="Remove attribute"
                                                        onClick={() => removeAttribute(index)}
                                                    >
                                                        <FiTrash2 size={16} />
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* PRODUCT INFORMATION */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Product Information</h5></Card.Header>
                            <Card.Body>
                                <Row>


                                    <Col md={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Product Title <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="title"
                                                value={formData.title ?? ""}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Product Code</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.product_code || ""}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sell Price ($) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="sell_price_dollar"
                                                value={formData.sell_price_dollar}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    {formData.selling_style === "Auction" && (
                                        <Col md={3}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Auction Min Price ($)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    name="auction_min_price"
                                                    value={formData.auction_min_price}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    )}

                                    <Col md={3}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Quantity</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="product_qty"
                                                value={formData.product_qty}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Condition</Form.Label>
                                            <Form.Select name="product_condition" value={formData.product_condition} onChange={handleChange}>
                                                <option value="new">New</option>
                                                <option value="used">Used</option>
                                                <option value="refurbished">Refurbished</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* SHORT DETAILS */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Short Details</h5></Card.Header>
                            <Card.Body>
                                <SunEditor
                                    setContents={formData.short_description}
                                    onChange={handleShortDescriptionChange}
                                    setOptions={{ buttonList: editorButtonList, height: 180 }}
                                />
                            </Card.Body>
                        </Card>

                        {/* MAIN IMAGE */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Main Image <span className="text-danger">*</span></h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={3}>
                                        {mainImagePreview && <img src={mainImagePreview} alt="Main" style={{ width: "100%", height: "150px", objectFit: "contain" }} />}
                                    </Col>
                                    <Col md={9}>
                                        <Form.Control type="file" accept="image/*" onChange={handleMainImageChange} required={!mainImagePreview} />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* GALLERY */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Product Gallery</h5></Card.Header>
                            <Card.Body>
                                <GalleryUpload pId={formData.id} images={existingImages} />
                            </Card.Body>
                        </Card>

                        {/* LONG DESCRIPTION */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Long Description <span className="text-danger">*</span></h5></Card.Header>
                            <Card.Body>
                                <SunEditor
                                    setContents={formData.description}
                                    onChange={handleDescriptionChange}
                                    setOptions={{ buttonList: editorButtonList, height: 300 }}
                                />
                            </Card.Body>
                        </Card>

                        {/* SUBMIT BUTTONS */}
                        <Card className="mb-5 shadow-sm">
                            <Card.Body className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
                                <Button type="submit" variant="primary" disabled={saving}>
                                    {saving ? <Spinner size="sm" animation="border" /> : "Update Product"}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Form>
                </Col>
            </Row>
        </PageLayout>
    );
}